import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import type { DBCustomerOwner } from "@/lib/db-types";

export interface CustomerOwnerWithUser extends DBCustomerOwner {
  user: { id: string; full_name: string; avatar_url: string | null } | null;
}

const SELECT_WITH_USER = "*, user:users!customer_owners_user_id_fkey(id, full_name, avatar_url)";

/** Eiere for én kunde (kundekortets hero). */
export function useCustomerOwners(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-owners", customerId],
    queryFn: async (): Promise<CustomerOwnerWithUser[]> => {
      const { data, error } = await supabase
        .from("customer_owners")
        .select(SELECT_WITH_USER)
        .eq("customer_id", customerId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as CustomerOwnerWithUser[];
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Alle kundeeiere. Brukes av kundelisten og (fase 2 og 3) køen. Ubundet lesing,
 * derfor fetchAllPaginated: tabellen har flere rader per kunde og kan passere
 * 1000 uten at noen legger merke til det.
 */
export function useAllCustomerOwners(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ["customer-owners"],
    queryFn: () =>
      fetchAllPaginated<CustomerOwnerWithUser>("customer_owners", SELECT_WITH_USER),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Bytter hele eiersettet via RPC set_customer_owners. RPC-en er aktør-sikker
 * (current_user_id()) og logger ÉN customer_owners.changed med forrige og nytt sett.
 */
export function useSetCustomerOwners() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { customerId: string; userIds: string[]; salesUnitId?: string | null }) => {
      const { data, error } = await supabase.rpc("set_customer_owners", {
        p_customer_id: vars.customerId,
        p_user_ids: vars.userIds,
        p_sales_unit_id: vars.salesUnitId ?? null,
      });
      if (error) throw error;
      return (data ?? []) as DBCustomerOwner[];
    },
    onSuccess: async (_rows, vars) => {
      await invalidateFor(qc, "customer_owners", { customerId: vars.customerId });
    },
    onError: (err: Error) => toast.error(`Kunne ikke endre eiere: ${err.message}`),
  });
}
